/* iter55x — Welcome overlay shown on a first-time user's first entry
 * (or any time they land on a genuinely empty project — zero
 * characters + zero non-empty chapters). Presents three friendly
 * cards so writers know exactly how to start:
 *
 *   1. Import a synopsis     — opens ImportSynopsisDialog
 *   2. Import a manuscript   — opens ImportManuscriptDialog
 *   3. Start with a template — opens StarterTemplatesDialog
 *
 * A "Start with a blank page" tertiary button dismisses the overlay
 * for users who really do want the cold empty state. Dismissal is
 * remembered per-project (via sessionStorage) so it doesn't nag on
 * every reload.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { FileText, FileUp, Sparkles, X, ArrowRight } from 'lucide-react';
import { usePW } from '@/lib/store';
import StarterTemplatesDialog from './StarterTemplatesDialog';

const CARDS = [
  {
    id: 'synopsis',
    icon: FileText,
    tint: 'from-emerald-500/15 to-emerald-500/5 text-emerald-600',
    title: 'Import a synopsis',
    body: 'Paste or upload a three-act summary. Claude extracts your acts, characters, and mood — everything drops into the Bible.',
    cta: 'Import synopsis',
  },
  {
    id: 'manuscript',
    icon: FileUp,
    tint: 'from-blue-500/15 to-blue-500/5 text-blue-600',
    title: 'Import a manuscript',
    body: 'Bring a .docx / .pdf / .md draft. Chapters and characters are auto-populated so you can pick up where you left off.',
    cta: 'Import manuscript',
  },
  {
    id: 'template',
    icon: Sparkles,
    tint: 'from-fuchsia-500/15 to-fuchsia-500/5 text-fuchsia-600',
    title: 'Start with a template',
    body: 'Pick a story framework (Three-Act, Hero\u2019s Journey, Save the Cat, or Story Circle). Chapters + character stubs are pre-filled.',
    cta: 'Browse templates',
  },
];

/** Is this project genuinely a fresh start? */
export function useIsEmptyProject() {
  const { project, characters, chapters } = usePW();
  return useMemo(() => {
    if (!project) return false;
    if ((characters || []).length > 0) return false;
    const anyContent = (chapters || []).some(
      (c) => (c.content || '').trim().length > 0,
    );
    return !anyContent;
  }, [project, characters, chapters]);
}

export default function WelcomeOverlay({
  onImportSynopsis, onImportManuscript,
  onDropFile,  // (file) → called when a .docx/txt is dropped so parent opens ImportSynopsisDialog
}) {
  const { project } = usePW();
  const isEmpty = useIsEmptyProject();
  const dismissKey = project ? `pw_welcome_dismissed_${project.id}` : null;
  const [dismissed, setDismissed] = useState(() =>
    dismissKey && window.sessionStorage.getItem(dismissKey) === '1',
  );
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  // Feb-2026 · Defer to OnboardingPromptModeDialog's onboarding chain
  // (prompt-mode picker → Getting Started Wizard → for Guided/Light
  // picks, the spotlight tour + Training Center) before ever showing,
  // so this richer import/template overlay never stacks on top of
  // them. That component is the sole, authoritative source for
  // 'onboarding-chain-complete' — it fires it immediately whenever it
  // determines nothing is coming (admin, picker permanently off, not
  // due for a reask) and otherwise only once the whole chain settles.
  const [wizardSettled, setWizardSettled] = useState(false);
  useEffect(() => {
    if (wizardSettled) return undefined;
    const onDone = () => setWizardSettled(true);
    window.addEventListener('onboarding-chain-complete', onDone);
    return () => window.removeEventListener('onboarding-chain-complete', onDone);
  }, [wizardSettled]);

  const open = isEmpty && !dismissed && !templatesOpen && wizardSettled;

  const dismiss = () => {
    if (dismissKey) {
      try { window.sessionStorage.setItem(dismissKey, '1'); } catch { /* ignore */ }
    }
    setDismissed(true);
  };

  const pick = (id) => {
    dismiss();
    if (id === 'synopsis')   onImportSynopsis?.();
    if (id === 'manuscript') onImportManuscript?.();
    if (id === 'template')   setTemplatesOpen(true);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => { if (!v) dismiss(); }}>
        <DialogContent className="max-w-3xl" data-testid="welcome-overlay">
          <DialogHeader>
            <DialogTitle className="text-[18px]">Welcome to The Loom</DialogTitle>
            <DialogDescription>
              Your Bible is empty and your chapters are blank &mdash; let&apos;s change that. How would you like to start?
            </DialogDescription>
          </DialogHeader>

          <div className="mt-2 grid gap-3 md:grid-cols-3">
            {CARDS.map(({ id, icon: Icon, tint, title, body, cta }) => (
              <button
                key={id}
                type="button"
                onClick={() => pick(id)}
                data-testid={`welcome-card-${id}`}
                className="group flex flex-col items-start rounded-lg border border-border bg-card p-4 text-left transition-shadow hover:border-primary/50 hover:shadow-panel-active"
              >
                <div className={`mb-3 flex h-10 w-10 items-center justify-center rounded-md bg-gradient-to-br ${tint}`}>
                  <Icon className="h-5 w-5" />
                </div>
                <div className="text-[13.5px] font-semibold">{title}</div>
                <p className="mt-1 text-[11.5px] leading-relaxed text-muted-foreground">
                  {body}
                </p>
                <span className="mt-3 inline-flex items-center gap-1 text-[11px] font-semibold text-primary group-hover:gap-2 transition-all">
                  {cta} <ArrowRight className="h-3 w-3" />
                </span>
              </button>
            ))}
          </div>

          {/* Drag-and-drop synopsis / manuscript zone */}
          <div
            role="button"
            tabIndex={0}
            data-testid="welcome-drop-zone"
            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); }}
            onDragLeave={(e) => { e.stopPropagation(); setDragOver(false); }}
            onDrop={(e) => {
              e.preventDefault(); e.stopPropagation(); setDragOver(false);
              const file = e.dataTransfer?.files?.[0];
              if (!file) return;
              const ext = (file.name.split('.').pop() || '').toLowerCase();
              if (!['docx','doc','pdf','rtf','txt','md','markdown'].includes(ext)) {
                return;
              }
              dismiss();
              onImportSynopsis?.();
              onDropFile?.(file);
            }}
            onClick={() => { dismiss(); onImportSynopsis?.(); }}
            onKeyDown={(e) => e.key === 'Enter' && pick('synopsis')}
            className={`mt-1 flex w-full items-center gap-3 rounded-lg border-2 border-dashed px-5 py-4
                       cursor-pointer transition-all select-none
                       ${dragOver
                          ? 'border-primary bg-primary/5 shadow-inner'
                          : 'border-border hover:border-primary/50 hover:bg-muted/30'}`}
          >
            <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md transition-colors
                            ${dragOver ? 'bg-primary/10' : 'bg-muted'}`}>
              <FileUp className={`h-4 w-4 ${dragOver ? 'text-primary' : 'text-muted-foreground'}`} />
            </div>
            <div className="flex-1 text-left">
              <p className="text-[13px] font-semibold text-foreground">
                {dragOver ? 'Release to load your synopsis' : 'Drop a synopsis or manuscript here'}
              </p>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                .docx, .pdf, .txt, .md — Claude will scaffold your chapters from it
              </p>
            </div>
            <span className="text-[11px] font-medium text-primary whitespace-nowrap">
              {dragOver ? '↓ drop' : 'or click →'}
            </span>
          </div>

          <div className="mt-2 flex justify-end">
            <Button
              variant="ghost"
              size="sm"
              onClick={dismiss}
              className="h-7 gap-1 text-[11px] text-muted-foreground"
              data-testid="welcome-dismiss"
            >
              <X className="h-3 w-3" /> Start with a blank page
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <StarterTemplatesDialog
        open={templatesOpen}
        onOpenChange={setTemplatesOpen}
        onApplied={dismiss}
      />
    </>
  );
}
